// Fatia S5b: marca própria da agência (nome, cor e logo) com prévia do cabeçalho de relatório.
import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ImageUp, Palette } from 'lucide-react'
import { errorMessage } from '../../lib/api'
import { ErrorState, Loading, PageHead } from '../../ui'
import { useToast } from '../../ui/toast'
import { useAccountBranding, useSaveBranding, useUploadLogo } from '../../features/agency/api'

const MAX = 2 * 1024 * 1024
const ACCEPT = ['image/png', 'image/jpeg', 'image/webp']
const HEX = /^#[0-9a-fA-F]{6}$/

export default function Branding() {
  const { accountId = '' } = useParams()
  const q = useAccountBranding(accountId)
  const save = useSaveBranding(accountId)
  const uploadLogo = useUploadLogo(accountId)
  const toast = useToast()
  const fileRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState('')
  const [color, setColor] = useState('#5b3df5')
  const [fileError, setFileError] = useState<string | null>(null)

  useEffect(() => {
    if (q.data) { setName(q.data.displayName); setColor(q.data.primaryColor) }
  }, [q.data])

  if (q.isLoading) return <><PageHead title="Marca própria" /><div className="card"><Loading lines={5} /></div></>
  if (q.error || !q.data) return <><PageHead title="Marca própria" /><ErrorState error={q.error} onRetry={() => q.refetch()} /></>

  const available = q.data.available
  const validColor = HEX.test(color)
  const onFile = (f: File | undefined) => {
    setFileError(null)
    if (!f) return
    if (!ACCEPT.includes(f.type)) return setFileError('Envie uma imagem PNG, JPG ou WEBP.')
    if (f.size > MAX) return setFileError('O logo pode ter no máximo 2 MB.')
    uploadLogo.mutate(f, { onSuccess: () => toast('Logo atualizado.'), onError: (e) => setFileError(errorMessage(e)) })
    if (fileRef.current) fileRef.current.value = ''
  }
  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    save.mutate({ displayName: name.trim(), primaryColor: color }, { onSuccess: () => toast('Marca salva.'), onError: (err) => toast(errorMessage(err)) })
  }

  return (
    <>
      <PageHead title="Marca própria" sub="Seu nome, cor e logo nos relatórios e no portal dos seus clientes." />
      {!available && (
        <div className="callout amber" style={{ marginBottom: 16 }}>
          <Palette size={18} style={{ flexShrink: 0 }} />
          <div>
            <strong>Marca própria faz parte dos planos para agências.</strong>{' '}
            Faça upgrade para mostrar a sua marca aos clientes. <Link to={`/app/conta/${accountId}/plano`} className="strong" style={{ color: 'var(--primary)' }}>Ver planos</Link>
          </div>
        </div>
      )}

      <div className="grid g-main">
        <form className="card stack" onSubmit={submit} aria-disabled={!available}>
          <div className="field">
            <label htmlFor="bn">Nome exibido</label>
            <input id="bn" className="input" maxLength={60} value={name} disabled={!available} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Agência Foguete" />
          </div>
          <div className="field">
            <label htmlFor="bc">Cor principal</label>
            <div className="row" style={{ gap: 8 }}>
              <input type="color" aria-label="Escolher cor" value={validColor ? color : '#5b3df5'} disabled={!available} onChange={(e) => setColor(e.target.value)} style={{ width: 44, height: 40, padding: 2, border: '1px solid var(--border-strong)', borderRadius: 10, background: 'white' }} />
              <input id="bc" className="input" style={{ maxWidth: 140 }} value={color} disabled={!available} onChange={(e) => setColor(e.target.value)} />
            </div>
            {!validColor && <span className="form-error small">Use o formato #RRGGBB.</span>}
          </div>
          <div className="field">
            <label>Logo</label>
            <div className="row wrap" style={{ gap: 12 }}>
              <div style={{ width: 72, height: 72, borderRadius: 12, border: '1px dashed var(--border-strong)', display: 'grid', placeItems: 'center', background: 'var(--surface-2)', overflow: 'hidden' }}>
                {q.data.logoUrl ? <img src={q.data.logoUrl} alt="Logo atual" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : <ImageUp size={22} className="muted" />}
              </div>
              <div>
                <input ref={fileRef} type="file" accept={ACCEPT.join(',')} hidden onChange={(e) => onFile(e.target.files?.[0])} />
                <button type="button" className="btn sm" disabled={!available || uploadLogo.isPending} onClick={() => fileRef.current?.click()}>
                  {uploadLogo.isPending ? 'Enviando…' : q.data.logoUrl ? 'Trocar logo' : 'Enviar logo'}
                </button>
                <div className="small muted mt-8">PNG, JPG ou WEBP, até 2 MB. Fundo transparente fica melhor.</div>
                {fileError && <div className="form-error small mt-8" role="alert">{fileError}</div>}
              </div>
            </div>
          </div>
          <div className="form-actions">
            <button className="btn primary" type="submit" disabled={!available || !validColor || name.trim().length < 2 || save.isPending}>{save.isPending ? 'Salvando…' : 'Salvar marca'}</button>
          </div>
        </form>

        <div className="stack">
          <h3>Prévia do relatório</h3>
          <div className="card flush" aria-label="Prévia do cabeçalho do relatório">
            <div style={{ background: validColor ? color : '#5b3df5', padding: '16px 20px', color: 'white', display: 'flex', alignItems: 'center', gap: 12 }}>
              {q.data.logoUrl
                ? <img src={q.data.logoUrl} alt="" style={{ height: 34, maxWidth: 120, objectFit: 'contain', background: 'rgba(255,255,255,.9)', borderRadius: 8, padding: 3 }} />
                : <span style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,.2)', display: 'grid', placeItems: 'center', fontWeight: 700 }}>{(name || 'A').slice(0, 1).toUpperCase()}</span>}
              <strong style={{ fontSize: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || 'Sua agência'}</strong>
            </div>
            <div style={{ padding: 20 }}>
              <div className="small muted">Relatório mensal</div>
              <h2 style={{ marginTop: 4 }}>Doce Ateliê · setembro</h2>
              <div className="grid g-2 mt-16" style={{ gap: 10 }}>
                <div><div className="small muted">Investido</div><div className="strong">R$ 1.420</div></div>
                <div><div className="small muted">Conversas no WhatsApp</div><div className="strong">96</div></div>
              </div>
              <div className="small muted mt-16">Preparado por {name || 'sua agência'}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
